import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { db } from '../../firebase/config';
import { doc, updateDoc, serverTimestamp, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { NotificationService } from '../../services/NotificationService';
import WorkRequestComments from './WorkRequestComments';
import WorkRequestFeedback from './WorkRequestFeedback';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../models/School';
import { COLLECTIONS } from '../../firebase/collections';

const CATEGORY_TRANSLATIONS = {
  plumbing: 'Plomberie', electrical: 'Électricité', carpentry: 'Menuiserie',
  painting: 'Peinture', hvac: 'Climatisation', it: 'Informatique',
  cleaning: 'Nettoyage', gardening: 'Jardinage', locksmith: 'Serrurerie',
  other: 'Autre'
};

const STATUS_DISPLAY = {
  pending: 'En attente', in_progress: 'En cours', completed: 'Terminé', cancelled: 'Annulé',
  'En attente': 'En attente', 'En cours': 'En cours', 'Terminé': 'Terminé', 'Annulé': 'Annulé'
};

const PRIORITY_DISPLAY = {
  low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente'
};

const extractRoom = (loc) => {
  if (!loc) return 'Non spécifié';
  if (typeof loc === 'object') return loc.room || loc.building || 'Non spécifié';
  const idx = loc.lastIndexOf('Salle ');
  return idx !== -1 ? loc.substring(idx) : loc;
};

const translateCategory = (v) => {
  if (!v) return 'Non spécifié';
  return CATEGORY_TRANSLATIONS[v.toLowerCase()] || CATEGORY_TRANSLATIONS[v] || v;
};

const isSet = (v) => v && v !== 'Non spécifié' && v !== 'Non défini';

const WorkRequestHandler = ({ request, onClose, onUpdateSuccess }) => {
  console.log('%c[COMPOSANT UTILISÉ] WorkRequestHandler (work-requests)', 'background: #ff6b6b; color: white; padding: 2px 5px; border-radius: 3px;');
  console.log('Request:', request);
  const [status, setStatus] = useState(request?.status || 'pending');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [requestingService, setRequestingService] = useState(null);
  const [technicalService, setTechnicalService] = useState(null);
  const [concernedService, setConcernedService] = useState(null);
  const { currentUser, userRole } = useAuth();

  // Vérifier si l'utilisateur est un service technique
  // Accepte plusieurs formats possibles pour le rôle technique
  const isTechnicalService = 
    userRole === USER_ROLES.TECHNICAL_SERVICE || 
    userRole === USER_ROLES.TECHNICAL || 
    userRole === "technical";
  
  // Vérifier si l'utilisateur est un demandeur et s'il est le créateur de la demande
  const isRequester = userRole === USER_ROLES.REQUESTER || userRole === "requester";
  const isRequestCreator = isRequester && request?.createdBy === currentUser?.uid;
  
  // Déterminer si l'utilisateur peut voir les détails complets de la demande
  const canViewDetails = isTechnicalService || isRequestCreator;
  
  console.log('Est service technique:', isTechnicalService, 'userRole:', userRole, 
    'Est demandeur:', isRequester, 'Est créateur:', isRequestCreator,
    'Peut voir détails:', canViewDetails);

  useEffect(() => {
    const fetchServices = async () => {
      if (request) {
        try {
          console.log('Données de la demande:', request);
          console.log('Service concerné dans la demande:', {
            concernedServiceId: request.concernedServiceId,
            concernedServiceName: request.concernedServiceName,
            concernedService: request.concernedService
          });
          
          // Récupérer le service demandeur
          console.log('Recherche du service demandeur avec les données:', {
            requestingServiceId: request.requestingServiceId,
            requestingServiceName: request.requestingServiceName,
            serviceName: request.serviceName,
            createdBy: request.createdBy
          });
          
          if (request.requestingServiceId) {
            const requestingServiceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, request.requestingServiceId));
            if (requestingServiceDoc.exists()) {
              setRequestingService(requestingServiceDoc.data());
              console.log('Service demandeur trouvé par requestingServiceId:', requestingServiceDoc.data());
            } else {
              // Essayer de trouver dans la collection USERS si c'est un service demandeur
              const usersQuery = query(collection(db, COLLECTIONS.USERS), where('serviceId', '==', request.requestingServiceId));
              const usersSnapshot = await getDocs(usersQuery);
              if (!usersSnapshot.empty) {
                setRequestingService(usersSnapshot.docs[0].data());
                console.log('Service demandeur trouvé par serviceId dans USERS:', usersSnapshot.docs[0].data());
              } else if (request.requestingServiceName) {
                // Si on a juste le nom mais pas l'objet complet
                setRequestingService({ name: request.requestingServiceName });
                console.log('Service demandeur défini par requestingServiceName:', request.requestingServiceName);
              }
            }
          } else if (request.createdBy) {
            // Essayer de trouver le service par l'utilisateur qui a créé la demande
            try {
              const creatorDoc = await getDoc(doc(db, COLLECTIONS.USERS, request.createdBy));
              if (creatorDoc.exists()) {
                const creatorData = creatorDoc.data();
                console.log('Créateur de la demande trouvé:', creatorData);
                
                if (creatorData.serviceName) {
                  setRequestingService({ name: creatorData.serviceName });
                  console.log('Service demandeur défini par le service du créateur:', creatorData.serviceName);
                } else if (creatorData.serviceId) {
                  // Chercher le service associé à l'utilisateur
                  const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, creatorData.serviceId));
                  if (serviceDoc.exists()) {
                    setRequestingService(serviceDoc.data());
                    console.log('Service demandeur trouvé par serviceId du créateur:', serviceDoc.data());
                  } else {
                    // Essayer de trouver un utilisateur avec ce serviceId pour obtenir le nom du service
                    const usersWithServiceQuery = query(collection(db, COLLECTIONS.USERS), where('serviceId', '==', creatorData.serviceId));
                    const usersWithServiceSnapshot = await getDocs(usersWithServiceQuery);
                    if (!usersWithServiceSnapshot.empty) {
                      // Parcourir tous les utilisateurs pour trouver un nom de service
                      for (const userDoc of usersWithServiceSnapshot.docs) {
                        const userData = userDoc.data();
                        if (userData.serviceName) {
                          setRequestingService({ name: userData.serviceName });
                          console.log('Service demandeur trouvé via un autre utilisateur avec le même serviceId:', userData.serviceName);
                          break;
                        }
                      }
                      
                      // Si aucun nom de service n'a été trouvé, utiliser le nom d'utilisateur comme fallback
                      if (!requestingService?.name) {
                        const userData = usersWithServiceSnapshot.docs[0].data();
                        setRequestingService({ 
                          name: userData.displayName || userData.email || 'Service demandeur' 
                        });
                      }
                    } else {
                      // Si aucun utilisateur n'est trouvé, utiliser le nom d'utilisateur du créateur
                      setRequestingService({ 
                        name: creatorData.displayName || creatorData.email || 'Service demandeur' 
                      });
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Erreur lors de la recherche du créateur:', error);
            }
          } else if (request.requestingServiceName) {
            // Si on a juste le nom mais pas l'ID
            setRequestingService({ name: request.requestingServiceName });
            console.log('Service demandeur défini par requestingServiceName:', request.requestingServiceName);
          } else if (request.serviceName) {
            // Utiliser le champ serviceName comme fallback
            console.log('Utilisation du champ serviceName comme nom du service demandeur:', request.serviceName);
            setRequestingService({ name: request.serviceName });
          } else {
            // Si aucun service demandeur n'est identifié, utiliser "Service demandeur" comme valeur par défaut
            console.log('Aucun service demandeur identifié, utilisation de la valeur par défaut');
            setRequestingService({ name: 'Service demandeur' });
          }

          // Récupérer le service technique (peut être assignedServiceId dans certains cas)
          const technicalId = request.technicalServiceId || request.assignedServiceId;
          if (technicalId) {
            const technicalServiceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, technicalId));
            if (technicalServiceDoc.exists()) {
              setTechnicalService(technicalServiceDoc.data());
            } else {
              // Essayer de trouver dans la collection USERS
              const usersQuery = query(collection(db, COLLECTIONS.USERS), where('serviceId', '==', technicalId));
              const usersSnapshot = await getDocs(usersQuery);
              if (!usersSnapshot.empty) {
                setTechnicalService(usersSnapshot.docs[0].data());
              } else if (request.technicalServiceName) {
                setTechnicalService({ name: request.technicalServiceName });
              }
            }
          } else if (request.technicalServiceName) {
            setTechnicalService({ name: request.technicalServiceName });
          }

          // Récupérer le service concerné
          if (request.concernedServiceId) {
            const concernedServiceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, request.concernedServiceId));
            if (concernedServiceDoc.exists()) {
              setConcernedService(concernedServiceDoc.data());
              console.log('Service concerné trouvé par concernedServiceId:', concernedServiceDoc.data());
            } else {
              // Essayer de trouver dans la collection USERS
              const usersQuery = query(collection(db, COLLECTIONS.USERS), where('serviceId', '==', request.concernedServiceId));
              const usersSnapshot = await getDocs(usersQuery);
              if (!usersSnapshot.empty) {
                // Parcourir tous les utilisateurs pour trouver un nom de service
                for (const userDoc of usersSnapshot.docs) {
                  const userData = userDoc.data();
                  if (userData.serviceName) {
                    setConcernedService({ name: userData.serviceName });
                    console.log('Service concerné trouvé via un utilisateur avec le serviceId:', userData.serviceName);
                    break;
                  }
                }
                
                // Si aucun nom de service n'a été trouvé, utiliser le nom d'utilisateur comme fallback
                if (!concernedService?.name) {
                  const userData = usersSnapshot.docs[0].data();
                  setConcernedService({ 
                    name: userData.displayName || userData.email || userData.serviceName || 'Service concerné' 
                  });
                  console.log('Service concerné défini par les données utilisateur:', concernedService);
                }
              } else if (request.concernedServiceName) {
                setConcernedService({ name: request.concernedServiceName });
                console.log('Service concerné défini par concernedServiceName:', request.concernedServiceName);
              }
            }
          } else if (request.concernedServiceName) {
            setConcernedService({ name: request.concernedServiceName });
            console.log('Service concerné défini par concernedServiceName:', request.concernedServiceName);
          } else if (request.concernedService && request.concernedService.name) {
            setConcernedService({ name: request.concernedService.name });
            console.log('Service concerné défini par request.concernedService.name:', request.concernedService.name);
          } else if (request.serviceName && request.serviceName !== requestingService?.name) {
            // Si le serviceName est différent du service demandeur, c'est peut-être le service concerné
            setConcernedService({ name: request.serviceName });
            console.log('Service concerné défini par serviceName:', request.serviceName);
          } else if (request.serviceId && request.serviceId !== request.requestingServiceId) {
            // Essayer de trouver le service par serviceId
            try {
              const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, request.serviceId));
              if (serviceDoc.exists()) {
                setConcernedService(serviceDoc.data());
                console.log('Service concerné trouvé par serviceId:', serviceDoc.data());
              } else {
                // Chercher un utilisateur avec ce serviceId
                const usersQuery = query(collection(db, COLLECTIONS.USERS), where('serviceId', '==', request.serviceId));
                const usersSnapshot = await getDocs(usersQuery);
                if (!usersSnapshot.empty) {
                  for (const userDoc of usersSnapshot.docs) {
                    const userData = userDoc.data();
                    if (userData.serviceName) {
                      setConcernedService({ name: userData.serviceName });
                      console.log('Service concerné trouvé via un utilisateur avec serviceId:', userData.serviceName);
                      break;
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Erreur lors de la recherche du service concerné par serviceId:', error);
            }
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des services:', error);
        }
      }
    };

    fetchServices();
  }, [request]);

  // Mettre à jour le statut quand la demande change
  useEffect(() => {
    if (request) {
      setStatus(request.status || 'pending');
      setComment('');
      setError('');
      setTabValue(0);
    }
  }, [request]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Si pas de demande, ne rien afficher
  if (!request) {
    return null;
  }

  const handleSubmit = async () => {
    if (!request.id) {
      setError('ID de la demande manquant');
      return;
    }

    if (!(isTechnicalService || isRequestCreator)) {
      setError("Vous n'avez pas les permissions nécessaires pour mettre à jour cette demande");
      return;
    }

    setLoading(true);
    setError('');

    try {
      const oldStatus = request.status;
      
      // Déterminer l'ID du service technique (peut être technicalServiceId ou assignedServiceId)
      const technicalId = request.technicalServiceId || request.assignedServiceId || currentUser?.uid;
      const serviceName = technicalService?.name || 'Service technique';
      
      console.log('Mise à jour de la demande avec:', {
        status: isTechnicalService ? status : request.status,
        technicalId,
        serviceName
      });
      
      try {
        // Mettre à jour la demande
        const requestRef = doc(db, COLLECTIONS.WORK_REQUESTS, request.id);
        
        // Préparer les données à mettre à jour
        const updateData = {
          lastComment: comment,
          updatedAt: serverTimestamp()
        };
        
        // Seul le service technique peut modifier le statut
        if (isTechnicalService) {
          updateData.status = status;
          updateData.handledBy = {
            id: technicalId,
            name: serviceName
          };
        }
        
        await updateDoc(requestRef, updateData);
        
        console.log('Mise à jour réussie de la demande');
      } catch (updateError) {
        // Vérifier si c'est une erreur de permissions ou une autre erreur Firebase
        if (updateError.code === 'permission-denied' || updateError.message.includes('Missing or insufficient permissions')) {
          setError('Vous n\'avez pas les permissions nécessaires pour mettre à jour cette demande. Contactez l\'administrateur.');
          console.error('Erreur de permissions lors de la mise à jour:', updateError);
          // Ne pas fermer la fenêtre en cas d'erreur de permissions
          setLoading(false);
          return;
        }
        // Pour les autres erreurs, on les propage
        throw updateError;
      }

      // Créer une notification de changement de statut
      try {
        await NotificationService.createStatusChangeNotification(
          request,
          oldStatus,
          status
        );
      } catch (notifError) {
        console.warn('Impossible de créer la notification, mais la mise à jour a réussi:', notifError);
        // On ne bloque pas le processus si la notification échoue
      }

      if (onUpdateSuccess) {
        onUpdateSuccess();
      }
      // Fermer la fenêtre après la mise à jour réussie
      onClose();
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      setError('Une erreur est survenue lors de la mise à jour de la demande: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
      case 'En attente':
        return '#FFA726';
      case 'in_progress':
      case 'En cours':
        return '#42A5F5';
      case 'completed':
      case 'Terminé':
        return '#66BB6A';
      case 'cancelled':
      case 'Annulé':
        return '#EF5350';
      default:
        return '#9E9E9E';
    }
  };

  return (
    <Dialog 
      open={Boolean(request)} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Box>
          {isTechnicalService ? 'Traiter la demande' : (isRequestCreator ? 'Suivre ma demande' : 'Détails de la demande')}
          <Typography variant="subtitle2" sx={{ opacity: 0.9, mt: 0.5 }}>
            De : {isSet(requestingService?.name) ? requestingService.name : isSet(request.requestingServiceName) ? request.requestingServiceName : (request.createdByName || 'Demandeur')} 
            → Pour : {technicalService?.name || request.technicalServiceName || request.assignedServiceName || 'Service technique'}
          </Typography>
          {/* ID masqué pour une meilleure interface utilisateur */}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={onClose}
              >
                Fermer
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Informations" />
          <Tab label="Commentaires" />
          {request.status === 'completed' || request.status === 'Terminé' ? <Tab label="Évaluation" /> : null}
        </Tabs>

        {tabValue === 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Informations :
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Chip
              label={STATUS_DISPLAY[request.status] || request.status || 'En attente'}
              sx={{
                bgcolor: getStatusColor(request.status),
                color: 'white',
                mb: 1
              }}
            />
            {request.priority && (
              <Chip
                label={PRIORITY_DISPLAY[request.priority] || request.priority}
                sx={{ ml: 1, mb: 1, bgcolor: request.priority === 'urgent' || request.priority === 'high' ? '#EF5350' : request.priority === 'medium' ? '#FFA726' : '#66BB6A', color: 'white' }}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary">
            Type : {translateCategory(request.problemType || request.problem || request.category)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Service demandeur : {isSet(requestingService?.name) ? requestingService.name : isSet(request.requestingServiceName) ? request.requestingServiceName : (request.createdByName || request.serviceName || 'Demandeur')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Service technique : {technicalService?.name || 'Service technique'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Service concerné : {concernedService?.name || request.concernedServiceName || (request.concernedService?.name) || (request.concernedService && typeof request.concernedService === 'string' ? request.concernedService : (request.serviceName && request.serviceName !== requestingService?.name ? request.serviceName : (technicalService?.name || 'Service technique')))}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Localisation : {extractRoom(request.location) !== 'Non spécifié' ? extractRoom(request.location) : (request.locationText || 'Non spécifié')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
            Description : {request.description || 'Aucune description'}
          </Typography>
          
          {request.lastComment && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 1, p: 1, bgcolor: 'rgba(0,0,0,0.03)', borderLeft: '3px solid', borderColor: 'primary.main' }}>
              <strong>Dernier commentaire :</strong> {request.lastComment}
            </Typography>
          )}
        </Box>
        )}

        {tabValue === 1 && (
          <WorkRequestComments 
            workRequest={request} 
            onCommentAdded={() => {
              if (onUpdateSuccess) {
                onUpdateSuccess();
              }
            }} 
          />
        )}

        {tabValue === 2 && request.status === 'completed' && (
          <WorkRequestFeedback 
            workRequest={request} 
            onFeedbackSubmitted={() => {
              if (onUpdateSuccess) {
                onUpdateSuccess();
              }
            }} 
          />
        )}

        {(isTechnicalService || isRequestCreator) && tabValue === 0 && (
          <>
            <Box sx={{ mt: 3, mb: 2, borderTop: '1px dashed rgba(0,0,0,0.1)', pt: 2 }}>
              <Typography variant="subtitle1" gutterBottom color="primary">
                {isTechnicalService ? 'Gestion de la demande' : 'Ajouter un message'}
              </Typography>
            </Box>
            
            {isTechnicalService && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Modifier le statut</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="Modifier le statut"
                  disabled={loading}
                >
                  <MenuItem value="pending">En attente</MenuItem>
                  <MenuItem value="in_progress">En cours</MenuItem>
                  <MenuItem value="completed">Terminé</MenuItem>
                  <MenuItem value="cancelled">Annulé</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              multiline
              rows={4}
              margin="normal"
              label="Message pour le demandeur"
              placeholder="Ajoutez un message ou des informations pour le service demandeur..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={loading}
              helperText="Ce message sera visible par le service demandeur"
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Fermer
        </Button>
        {(isTechnicalService || isRequestCreator) && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {isTechnicalService ? 'Mettre à jour' : 'Envoyer message'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WorkRequestHandler;
